import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import { Text } from '@/hen-ui/atomics/Text';
import { Separator } from '@/hen-ui/atomics/Separator';

const LINE_COLOR_CLASSES = {
  default: 'bg-neutral-500',
  primary: 'bg-primary-100',
  secondary: 'bg-secondary-100',
  accent: 'bg-accent-200',
} as const;

const LINE_WIDTH_CLASSES = {
  thin: 'w-0.5',
  thick: 'w-1',
} as const;

export type LineColor = keyof typeof LINE_COLOR_CLASSES;
export type LineWidth = keyof typeof LINE_WIDTH_CLASSES;

export interface TimelineEventDescriptionProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  index: number;
  isLast: boolean;
  lineColor?: LineColor;
  lineWidth?: LineWidth;
}

const ICON_SIZE = '30px';

export const TimelineEventDescription = React.forwardRef<
  HTMLDivElement,
  TimelineEventDescriptionProps
>(
  (
    {
      index,
      isLast,
      lineColor = 'default',
      lineWidth = 'thin',
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <div className={cn('flex gap-xs', className)} ref={ref} {...props}>
        <div
          className='flex-shrink-0 flex items-center justify-center'
          style={{ width: ICON_SIZE }}
        >
          {!isLast && (
            <Separator
              orientation='vertical'
              decorative
              className={cn(
                'rounded-full',
                LINE_COLOR_CLASSES[lineColor],
                LINE_WIDTH_CLASSES[lineWidth],
              )}
            />
          )}
        </div>

        <div className='flex-1 pb-md'>
          <Text
            tag='div'
            variant='b4'
            id={`timeline-event-${index}-description`}
          >
            {children}
          </Text>
        </div>
      </div>
    );
  },
);

TimelineEventDescription.displayName = 'TimelineEventDescription';
